import { SOLUTION_SOURCES, type SolutionSource } from '../../shared/solution-source.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { loadAndAssert } from '../authz/access.js';
import { Exam } from '../exam.js';
import { ownMaxPoints } from '../utils/points-model.js';
import {
  type BoundingBoxContentType,
  deriveContentType,
  normalizeContentType,
} from '../../shared/bounding-box-content.js';
import { findLanguageUseSchemesForTask } from '../../shared/grammar-rules-resolution.js';

/**
 * Lightweight DTO for returning master solution data to the client.
 * This avoids BaseQL's field filtering issues with the "doc" wrapper
 * that older MasterSolution subdocuments may have in MongoDB.
 */
@Entity()
export class MasterSolutionDTO extends AdornisEntity {
  static override _class = 'MasterSolutionDTO';

  @Field(() => String)
  taskId!: string;

  @Field(() => String)
  taskNumber!: string;

  @Field(() => String)
  displayTaskNumber!: string;

  @Field(() => String)
  taskType: Maybe<string>;

  @Field(() => String)
  description!: string;

  @Field(() => String)
  masterSolutionContent!: string;

  @Field(() => String)
  gradingCriteria!: string;

  @Field(() => Float)
  gradingMaxPoints!: number;

  @Field(() => [String])
  descriptionImageIds!: string[];

  /**
   * JSON-serialized array of {pageNumber, x, y, width, height} (0-based page, percent 0-100 coordinates) —
   * one entry per task.boundingBoxes entry, in the same order as descriptionImageIds. Lets the client
   * overlay the task excerpt's box(es) on the full original exam page(s) in wide-canvas mode, instead of
   * only showing the pre-cropped images. null when the task has no description bounding boxes.
   */
  @Field(() => String)
  descriptionBoxesJson: Maybe<string>;

  @Field(() => [String])
  masterSolutionImageIds!: string[];

  @Field(() => [String])
  gradingSchemeImageIds!: string[];

  @Field(() => Float)
  masterSolutionConfidence: Maybe<number>;

  @Field(() => Float)
  gradingSchemeConfidence: Maybe<number>;

  @Field(() => Boolean)
  masterSolutionIsApproved: Maybe<boolean>;

  @Field(() => Boolean)
  gradingSchemeIsApproved: Maybe<boolean>;

  /** 'upload' | 'generated' per task — drives the AI badge and the confidence colouring in the canvas. */
  @Field(() => String)
  masterSolutionSource: Maybe<SolutionSource>;

  @Field(() => String)
  gradingSchemeSource: Maybe<SolutionSource>;

  /**
   * JSON-serialized array of {_id, criteria, maxPoints, confidence, isApproved, dependsOn, displayNumber} — every
   * LanguageUseScheme this task belongs to (solo 1-member and multi-member rules alike, every
   * language-use rule is a LanguageUseScheme now — see shared/grammar-rules-resolution.ts
   * findLanguageUseSchemesForTask). A task can belong to more than one. Passed as a JSON string rather
   * than nested entities, matching how other tree-shaped data already crosses this DTO boundary. null
   * when the task has no language-use rule at all.
   */
  @Field(() => String)
  languageUseGroupsJson: Maybe<string>;

  // Per-section content type, derived from each section's bounding boxes:
  // 'text' (load text), 'image' (load image, uneditable), 'both' (load image, edit reveals text).
  @Field(() => String)
  descriptionContentType!: BoundingBoxContentType;

  @Field(() => String)
  masterSolutionContentType!: BoundingBoxContentType;

  @Field(() => String)
  gradingSchemeContentType!: BoundingBoxContentType;

  @Field(() => Boolean)
  needsAnswer!: boolean;
}

/**
 * Extracts MasterSolution content from a raw MongoDB subdocument,
 * handling both the old "doc" wrapper format and the new flat format.
 */
function extractMsContent(rawMs: any): string {
  if (!rawMs) return '';

  // New format (after .toObject() fix): { _class, content, ... }
  if (typeof rawMs.content === 'string') return rawMs.content;

  // Old format (reactive entity wrapper): { doc: { _class, content, ... }, ... }
  if (rawMs.doc && typeof rawMs.doc.content === 'string') return rawMs.doc.content;

  return '';
}

function extractGradingScheme(rawGs: any): { criteria: string; maxPoints: number } {
  if (!rawGs) return { criteria: '', maxPoints: 0 };

  if (typeof rawGs.criteria === 'string') {
    return { criteria: rawGs.criteria, maxPoints: Number(rawGs.maxPoints) || 0 };
  }

  if (rawGs.doc && typeof rawGs.doc.criteria === 'string') {
    return { criteria: rawGs.doc.criteria, maxPoints: Number(rawGs.doc.maxPoints) || 0 };
  }

  return { criteria: '', maxPoints: 0 };
}

// Only image/both boxes have a crop worth displaying. Text boxes also carry a cropped imageId,
// but showing it would duplicate their transcribed text as an image — so exclude them here.
function extractBoundingBoxImageIds(raw: any): string[] {
  const source = raw?.doc || raw;
  const boxes = Array.isArray(source?.boundingBoxes) ? source.boundingBoxes : [];
  return boxes
    .filter((bb: any) => normalizeContentType(bb?.contentType) !== 'text')
    .map((bb: any) => bb?.imageId)
    .filter(Boolean);
}

// Percent (0-100) box coordinates + 0-based page number, converted from the entity's 0-1000-scale
// topLeft/bottomRight points — same conversion as the client's toPercentBox (structure-editor view),
// duplicated here since that util lives on the client side of the DTO boundary.
function extractDescriptionBoxes(
  boundingBoxes: any,
): Array<{ pageNumber: number; x: number; y: number; width: number; height: number }> {
  const boxes = Array.isArray(boundingBoxes) ? boundingBoxes : [];
  return boxes
    .map((bb: any) => {
      const topLeft = bb?.topLeft;
      const bottomRight = bb?.bottomRight;
      if (typeof topLeft?.x !== 'number' || typeof topLeft?.y !== 'number') return null;
      if (typeof bottomRight?.x !== 'number' || typeof bottomRight?.y !== 'number') return null;

      const x1 = topLeft.x / 10;
      const y1 = topLeft.y / 10;
      const x2 = bottomRight.x / 10;
      const y2 = bottomRight.y / 10;

      return {
        pageNumber: Math.max(0, Number(bb.pageNumber ?? 1) - 1),
        x: Math.max(0, Math.min(x1, 99)),
        y: Math.max(0, Math.min(y1, 99)),
        width: Math.max(1, Math.min(x2 - x1, 100)),
        height: Math.max(1, Math.min(y2 - y1, 100)),
      };
    })
    .filter((box): box is { pageNumber: number; x: number; y: number; width: number; height: number } => box !== null);
}

function extractConfidence(raw: any): number | undefined {
  const source = raw?.doc || raw;
  const val = source?.confidence;
  return typeof val === 'number' && Number.isFinite(val) ? val : undefined;
}

function extractIsApproved(raw: any): boolean {
  const source = raw?.doc || raw;
  return source?.isApproved === true;
}
/**
 * Per-task provenance. Falls back to inferring it for content written before the field existed: only an
 * extraction of an uploaded document produces bounding boxes, and generation only ever writes text.
 */
function extractSource(raw: any, contentField: 'content' | 'criteria'): SolutionSource | undefined {
  const source = raw?.doc || raw;
  if (!source) return undefined;
  if (SOLUTION_SOURCES.includes(source.source)) return source.source as SolutionSource;

  const boxes = source.boundingBoxes;
  if (Array.isArray(boxes) && boxes.length > 0) return 'upload';
  const text = source[contentField];
  return typeof text === 'string' && text.trim().length > 0 ? 'generated' : undefined;
}

// Aggregate the content types of a section's bounding boxes into a single display content type.
// `hasText` folds in the section's own text content (master-solution content / grading criteria) —
// the field where both OCR-aggregated box text and generated text are written. Whenever there is text
// to show we must not hide it, so a section with image boxes is reported as 'both' (text + image) and
// an otherwise text/empty section as 'text'. This keeps generated text visible even when the only
// boxes are 'image' (e.g. an OCR image region, or a manually uploaded image).
function deriveSectionContentType(boxes: any[] | undefined, hasText = false): BoundingBoxContentType {
  const list = Array.isArray(boxes) ? boxes : [];
  const boxType = deriveContentType(list.map((bb: any) => bb?.contentType));
  if (!hasText) return boxType;
  return boxType === 'image' || boxType === 'both' ? 'both' : 'text';
}

export const getMasterSolutionsQuery = registerQuery({
  operationName: 'getMasterSolutionsQuery',
  type: () => [MasterSolutionDTO],
  params: [{ name: 'examId', type: () => String }],

  resolve:
    (examId: string) =>
    async (selection: BaseQLSelectionSet<MasterSolutionDTO>): Promise<MasterSolutionDTO[]> => {
      await loadAndAssert(Exam, examId, 'read', { _id: 1 });
      const examCollection = await getCollection(Exam);
      const exam = await examCollection.findOne({ _id: examId });

      if (!exam?.tasks || !Array.isArray(exam.tasks)) {
        return [];
      }

      const languageUseSchemeRules: any[] = Array.isArray((exam as any).languageUseSchemeRules)
        ? (exam as any).languageUseSchemeRules
        : [];
      // Keyed by the same "|"-joined qualified path LanguageUseScheme.dependsOn now stores (see
      // build-exam-tasks.ts's collectAllTaskNumbers) — a bare local taskNumber like "a" is ambiguous
      // whenever it's reused as a subtask label under more than one parent. A task can belong to more
      // than one scheme, so this returns every match, not just the first.
      const findLanguageUseGroupsForTask = (qualifiedTaskNumber: string): any[] =>
        findLanguageUseSchemesForTask(qualifiedTaskNumber, languageUseSchemeRules);

      const results: MasterSolutionDTO[] = [];

      const processTasks = (tasks: any[], parentDisplayTaskNumber?: string, parentPath: string[] = []) => {
        for (let index = 0; index < tasks.length; index += 1) {
          const task = tasks[index];
          const tNum = String(task?.taskNumber ?? index + 1);
          const displayTaskNumber = parentDisplayTaskNumber ? `${parentDisplayTaskNumber}.${tNum}` : tNum;
          const qualifiedPath = [...parentPath, tNum];
          const taskId = String(task?._id ?? `task-${displayTaskNumber}-${index}`);
          const desc = task.description || '';
          const msContent = extractMsContent(task.masterSolution);
          const gradingScheme = extractGradingScheme(task.gradingScheme);

          const descriptionImageIds = Array.isArray(task.boundingBoxes)
            ? (task.boundingBoxes as any[]).map(bb => bb?.imageId).filter(Boolean)
            : [];
          const descriptionBoxes = extractDescriptionBoxes(task.boundingBoxes);

          const masterSolutionImageIds = extractBoundingBoxImageIds(task.masterSolution);
          const gradingSchemeImageIds = extractBoundingBoxImageIds(task.gradingScheme);
          const masterSolutionConfidence = extractConfidence(task.masterSolution);
          const gradingSchemeConfidence = extractConfidence(task.gradingScheme);
          const masterSolutionIsApproved = extractIsApproved(task.masterSolution);
          const gradingSchemeIsApproved = extractIsApproved(task.gradingScheme);
          const masterSolutionSource = extractSource(task.masterSolution, 'content');
          const gradingSchemeSource = extractSource(task.gradingScheme, 'criteria');
          const languageUseGroups = findLanguageUseGroupsForTask(qualifiedPath.join('|'));

          const descriptionContentType = deriveSectionContentType(task.boundingBoxes);
          const masterSolutionContentType = deriveSectionContentType(
            (task.masterSolution?.doc || task.masterSolution)?.boundingBoxes,
            Boolean(msContent?.trim()),
          );
          const gradingSchemeContentType = deriveSectionContentType(
            (task.gradingScheme?.doc || task.gradingScheme)?.boundingBoxes,
            Boolean(gradingScheme.criteria?.trim()),
          );

          results.push(
            new MasterSolutionDTO({
              _id: taskId,
              taskId,
              taskNumber: tNum,
              displayTaskNumber,
              taskType: typeof task?.taskType === 'string' ? task.taskType : undefined,
              description: desc,
              masterSolutionContent: msContent,
              gradingCriteria: gradingScheme.criteria,
              // This node's OWN points from the single source of truth (scheme→declared for a leaf), so the
              // downstream query-agent aggregation stays consistent with the exam's max-points model.
              gradingMaxPoints: ownMaxPoints(task),
              descriptionImageIds,
              descriptionBoxesJson: descriptionBoxes.length ? JSON.stringify(descriptionBoxes) : null,
              masterSolutionImageIds,
              gradingSchemeImageIds,
              masterSolutionConfidence,
              gradingSchemeConfidence,
              masterSolutionIsApproved,
              gradingSchemeIsApproved,
              masterSolutionSource,
              gradingSchemeSource,
              languageUseGroupsJson: languageUseGroups.length
                ? JSON.stringify(
                    languageUseGroups.map(group => ({
                      _id: group._id,
                      criteria: group.criteria,
                      maxPoints: group.maxPoints,
                      confidence: group.confidence,
                      isApproved: group.isApproved,
                      dependsOn: group.dependsOn,
                      displayNumber: group.displayNumber,
                    })),
                  )
                : null,
              descriptionContentType,
              masterSolutionContentType,
              gradingSchemeContentType,
              needsAnswer: task?.needsAnswer !== false,
            }),
          );

          // Also handle subTasks
          if (task.subTasks && Array.isArray(task.subTasks)) {
            processTasks(task.subTasks, displayTaskNumber, qualifiedPath);
          }
        }
      };

      processTasks(exam.tasks);
      logger.info({ examId, taskCount: results.length, context: 'getMasterSolutionsQuery' }, 'Found tasks');
      return results;
    },
});
